import type { CmsNavInput, CmsNavItem } from '@erp/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Card, Skeleton } from '../../components/shadcn/index.js';
import { Button, confirmDialog, FormField, Input } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';

const EMPTY: CmsNavInput = { label: '', targetType: 'page', target: '', location: 'header', sort: 0 };

export function CmsNavigationPage(): JSX.Element {
  const { t } = useTranslation('cms');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('cms.write');
  const [items, setItems] = useState<CmsNavItem[] | null>(null);
  const [form, setForm] = useState<CmsNavInput>(EMPTY);
  const [editId, setEditId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = (): void => {
    void api().get<{ items: CmsNavItem[] }>('/cms/navigation').then((r) => setItems(r.data.items)).catch(() => setItems([]));
  };
  useEffect(load, []);

  const startEdit = (it: CmsNavItem): void => {
    setEditId(it.id);
    setForm({ label: it.label, targetType: it.targetType, target: it.target, location: it.location, sort: it.sort });
  };
  const reset = (): void => { setEditId(null); setForm(EMPTY); };

  const save = async (): Promise<void> => {
    if (!form.label.trim() || !form.target.trim()) { toast.error(t('nav.invalid')); return; }
    setSaving(true);
    try {
      if (editId) await api().put(`/cms/navigation/${editId}`, form);
      else await api().post('/cms/navigation', form);
      toast.success(t('nav.saved'));
      reset(); load();
    } catch { toast.error(tc('errors:server')); }
    finally { setSaving(false); }
  };

  const remove = async (it: CmsNavItem): Promise<void> => {
    if (!(await confirmDialog({ message: t('nav.confirmDelete', { label: it.label }) }))) return;
    try { await api().delete(`/cms/navigation/${it.id}`); load(); } catch { toast.error(tc('errors:server')); }
  };

  const set = <K extends keyof CmsNavInput>(k: K, v: CmsNavInput[K]): void => setForm((f) => ({ ...f, [k]: v }));

  const section = (loc: 'header' | 'footer'): JSX.Element => {
    const list = (items ?? []).filter((i) => i.location === loc);
    return (
      <Card className="p-4">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-fg-muted">{t(`nav.location.${loc}`)}</h2>
        {list.length === 0 ? (
          <p className="text-sm text-fg-muted">{t('nav.none')}</p>
        ) : (
          <ul className="divide-y divide-border">
            {list.map((it) => (
              <li key={it.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <div className="min-w-0">
                  <span className="font-medium">{it.label}</span>
                  <span className="ms-2 font-mono text-xs text-fg-muted">{it.targetType === 'page' ? `/p/${it.target}` : it.target}</span>
                </div>
                {canWrite && (
                  <div className="flex flex-none gap-3">
                    <button type="button" className="text-xs text-brand hover:underline" onClick={() => startEdit(it)}>{tc('actions.edit')}</button>
                    <button type="button" className="text-xs text-danger hover:underline" onClick={() => void remove(it)}>{tc('actions.delete')}</button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('nav.heading')}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('nav.subtitle')}</p>
      </header>

      {!items ? <Card className="p-6"><Skeleton className="h-24 w-full" /></Card> : (
        <>
          {section('header')}
          {section('footer')}
        </>
      )}

      {canWrite && (
        <Card className="flex flex-col gap-3 p-4">
          <h2 className="text-sm font-semibold">{editId ? t('nav.editTitle') : t('nav.addTitle')}</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField id="nav-label" label={t('nav.label')} required>
              <Input id="nav-label" value={form.label} onChange={(e) => set('label', e.target.value)} maxLength={120} />
            </FormField>
            <FormField id="nav-target" label={form.targetType === 'page' ? t('nav.pageSlug') : t('nav.url')} required>
              <Input id="nav-target" value={form.target} onChange={(e) => set('target', e.target.value)} maxLength={2000} placeholder={form.targetType === 'page' ? 'about' : 'https://'} />
            </FormField>
            <FormField id="nav-type" label={t('nav.targetType')}>
              <select id="nav-type" value={form.targetType} onChange={(e) => set('targetType', e.target.value as 'page' | 'url')} className="h-9 w-full rounded-md border border-border bg-bg px-2 text-sm">
                <option value="page">{t('nav.typePage')}</option>
                <option value="url">{t('nav.typeUrl')}</option>
              </select>
            </FormField>
            <FormField id="nav-loc" label={t('nav.locationLabel')}>
              <select id="nav-loc" value={form.location} onChange={(e) => set('location', e.target.value as 'header' | 'footer')} className="h-9 w-full rounded-md border border-border bg-bg px-2 text-sm">
                <option value="header">{t('nav.location.header')}</option>
                <option value="footer">{t('nav.location.footer')}</option>
              </select>
            </FormField>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => void save()} loading={saving}>{editId ? tc('actions.save') : t('nav.add')}</Button>
            {editId && <Button variant="ghost" onClick={reset}>{tc('actions.cancel')}</Button>}
          </div>
        </Card>
      )}
    </div>
  );
}
